/**
 * @fileoverview The Sales Pipeline backlog table's filters.
 * @description The filter state the table keeps (search text, state, bank type, tier, lead rating,
 * branch band, backlog-only and archive status), its defaults, and the conversion into the list
 * endpoint's query (GET /sales/prospects), which leaves out every empty filter.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  ProspectArchiveStatus,
  ProspectBranchBand,
  ProspectLeadRating,
  ProspectListFilters,
} from '@/features/sales/types/prospect.types'

/** @description The table's filters as the person has set them. */
export interface BacklogFilters {
  query: string
  stateId: string
  bankTypeId: string
  tierCode: string
  leadRating: ProspectLeadRating | ''
  branchBand: ProspectBranchBand | ''
  /** True shows only prospects with no open target (the backlog proper). */
  backlogOnly: boolean
  archiveStatus: ProspectArchiveStatus
}

/** @description The filters with nothing narrowed: the accepted, unarchived prospects. */
export const DEFAULT_FILTERS: BacklogFilters = {
  query: '',
  stateId: '',
  bankTypeId: '',
  tierCode: '',
  leadRating: '',
  branchBand: '',
  backlogOnly: false,
  archiveStatus: 'ACTIVE',
}

/**
 * @description Whether any filter differs from the default.
 * @param {BacklogFilters} filters - The table's filters.
 * @returns {boolean} True when the table is narrowed.
 */
export function hasActiveFilters(filters: BacklogFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.stateId !== '' ||
    filters.bankTypeId !== '' ||
    filters.tierCode !== '' ||
    filters.leadRating !== '' ||
    filters.branchBand !== '' ||
    filters.backlogOnly ||
    filters.archiveStatus !== DEFAULT_FILTERS.archiveStatus
  )
}

/**
 * @description Turns the table's filters into the list endpoint's, leaving out the empty ones.
 * @param {BacklogFilters} filters - The table's filters, with the search already debounced.
 * @returns {ProspectListFilters} The list endpoint's filters.
 */
export function toListFilters(filters: BacklogFilters): ProspectListFilters {
  return {
    archiveStatus: filters.archiveStatus,
    ...(filters.query.trim() && { search: filters.query.trim() }),
    ...(filters.stateId && { stateId: filters.stateId }),
    ...(filters.bankTypeId && { bankTypeId: filters.bankTypeId }),
    ...(filters.tierCode && { tierCode: filters.tierCode }),
    ...(filters.leadRating && { leadRating: filters.leadRating }),
    ...(filters.branchBand && { branchBand: filters.branchBand }),
    ...(filters.backlogOnly && { backlog: true }),
  }
}
